import { ShapeRenderer, type ShapeSpec } from "./ShapeRenderer";

function TileSplit({ orientation = 0, small = false }: { orientation?: number; small?: boolean }) {
  return (
    <svg className={small ? "h-20 w-20" : "h-24 w-24"} viewBox="0 0 100 100" role="img" aria-label="Διαγώνιο tile">
      <rect x="3" y="3" width="94" height="94" rx="7" fill="rgb(248 250 252)" stroke="rgb(148 163 184)" strokeWidth="4" />
      <g transform={`rotate(${orientation * 90} 50 50)`}>
        <polygon points="3,3 97,3 3,97" fill="rgb(2 6 23)" />
        <polygon points="97,3 97,97 3,97" fill="rgb(248 250 252)" />
        <line x1="3" y1="97" x2="97" y2="3" stroke="rgb(15 23 42)" strokeWidth="3" />
      </g>
      <rect x="3" y="3" width="94" height="94" rx="7" fill="none" stroke="rgb(148 163 184)" strokeWidth="4" />
    </svg>
  );
}

function LineTile({ lines, small = false }: { lines: string[]; small?: boolean }) {
  const strokeProps = { stroke: "rgb(103 232 249)", strokeWidth: 9, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const has = new Set(lines);
  return (
    <svg className={small ? "h-20 w-20" : "h-24 w-24"} viewBox="0 0 100 100" role="img" aria-label="Γραμμικό tile">
      <rect x="6" y="6" width="88" height="88" rx="8" fill="rgb(2 6 23)" stroke="rgb(148 163 184)" strokeWidth="3" />
      {has.has("top") ? <line x1="24" y1="24" x2="76" y2="24" {...strokeProps} /> : null}
      {has.has("right") ? <line x1="76" y1="24" x2="76" y2="76" {...strokeProps} /> : null}
      {has.has("bottom") ? <line x1="24" y1="76" x2="76" y2="76" {...strokeProps} /> : null}
      {has.has("left") ? <line x1="24" y1="24" x2="24" y2="76" {...strokeProps} /> : null}
      {has.has("diagDown") ? <line x1="24" y1="24" x2="76" y2="76" {...strokeProps} /> : null}
      {has.has("diagUp") ? <line x1="24" y1="76" x2="76" y2="24" {...strokeProps} /> : null}
      {has.has("midH") ? <line x1="24" y1="50" x2="76" y2="50" {...strokeProps} /> : null}
      {has.has("midV") ? <line x1="50" y1="24" x2="50" y2="76" {...strokeProps} /> : null}
    </svg>
  );
}

function polygonPoints(shape: string) {
  if (shape === "triangle") return "50 14 88 84 12 84";
  if (shape === "invertedTriangle") return "12 16 88 16 50 86";
  if (shape === "diamond") return "50 10 90 50 50 90 10 50";
  if (shape === "octagon") return "32 10 68 10 90 32 90 68 68 90 32 90 10 68 10 32";
  return "16 16 84 16 84 84 16 84";
}

function NestedShapeTile({ outer, inner, small = false, classic = false }: { outer: string; inner: string; small?: boolean; classic?: boolean }) {
  const tileFill = classic ? "rgb(248 250 252)" : "rgb(2 6 23)";
  const tileStroke = classic ? "rgb(15 23 42)" : "rgb(51 65 85)";
  const outerStroke = classic ? "rgb(15 23 42)" : "rgb(103 232 249)";
  const innerStroke = classic ? "rgb(15 23 42)" : "rgb(248 250 252)";
  return (
    <svg className={small ? "h-20 w-20" : "h-24 w-24"} viewBox="0 0 100 100" role="img" aria-label="Tile με εσωτερικό και εξωτερικό σχήμα">
      <rect x="4" y="4" width="92" height="92" rx={classic ? "0" : "8"} fill={tileFill} stroke={tileStroke} strokeWidth={classic ? "0" : "2"} />
      {outer === "circle" ? (
        <circle cx="50" cy="50" r="34" fill="none" stroke={outerStroke} strokeWidth="5" />
      ) : (
        <polygon points={polygonPoints(outer)} fill="none" stroke={outerStroke} strokeWidth="5" strokeLinejoin="round" />
      )}
      {inner === "circle" ? (
        <circle cx="50" cy="50" r="13" fill="none" stroke={innerStroke} strokeWidth="4" />
      ) : (
        <polygon points={polygonPoints(inner)} fill="none" stroke={innerStroke} strokeWidth="4" strokeLinejoin="round" transform="translate(25 25) scale(0.5)" />
      )}
    </svg>
  );
}

function quadrantRect(region: string) {
  if (region === "top-right") return { x: 50, y: 0, width: 50, height: 50 };
  if (region === "bottom-right") return { x: 50, y: 50, width: 50, height: 50 };
  if (region === "bottom-left") return { x: 0, y: 50, width: 50, height: 50 };
  return { x: 0, y: 0, width: 50, height: 50 };
}

function QuadrantShapeTile({ shape, filledRegion, small = false }: { shape: string; filledRegion: string; small?: boolean }) {
  const clipId = `clip-${shape}-${filledRegion}-${small ? "s" : "l"}`;
  const fill = quadrantRect(filledRegion);
  const shapeElement =
    shape === "circle" ? (
      <circle cx="50" cy="50" r="35" />
    ) : (
      <polygon points={polygonPoints(shape)} />
    );

  return (
    <svg className={small ? "h-16 w-16" : "h-24 w-24"} viewBox="0 0 100 100" role="img" aria-label="Σχήμα με μαύρο τεταρτημόριο">
      <rect x="2" y="2" width="96" height="96" rx="8" fill="rgb(248 250 252)" stroke="rgb(203 213 225)" strokeWidth="2" />
      <defs>
        <clipPath id={clipId}>{shapeElement}</clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width="100" height="100" fill="rgb(248 250 252)" />
        <rect {...fill} fill="rgb(2 6 23)" />
        <line x1="50" y1="8" x2="50" y2="92" stroke="rgb(15 23 42)" strokeWidth="3" />
        <line x1="8" y1="50" x2="92" y2="50" stroke="rgb(15 23 42)" strokeWidth="3" />
      </g>
      {shape === "circle" ? (
        <circle cx="50" cy="50" r="35" fill="none" stroke="rgb(15 23 42)" strokeWidth="4" />
      ) : (
        <polygon points={polygonPoints(shape)} fill="none" stroke="rgb(15 23 42)" strokeWidth="4" strokeLinejoin="round" />
      )}
    </svg>
  );
}

function MatrixCell({ cell, classic = false }: { cell: unknown; classic?: boolean }) {
  if (cell === null || (typeof cell === "object" && cell && "kind" in cell && (cell as { kind: unknown }).kind === "question")) {
    return <span className={classic ? "text-4xl font-bold text-slate-950" : "text-3xl font-bold text-cyan-100"}>?</span>;
  }

  if (typeof cell === "object" && cell) {
    const spec = cell as Record<string, unknown>;
    if (spec.kind === "tileSplit") return <TileSplit orientation={Number(spec.orientation ?? 0)} small />;
    if (spec.kind === "lineTile") return <LineTile lines={Array.isArray(spec.lines) ? spec.lines.map(String) : []} small />;
    if (spec.kind === "nestedShape") return <NestedShapeTile outer={String(spec.outer ?? "triangle")} inner={String(spec.inner ?? "circle")} small classic={classic || spec.style === "classicLight"} />;
    if (spec.kind === "quadrantShape") return <QuadrantShapeTile shape={String(spec.shape ?? "square")} filledRegion={String(spec.filledRegion ?? "top-left")} small />;
  }

  return <ShapeRenderer spec={cell as ShapeSpec} />;
}

export function MatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = Array.isArray(visualSpec.cells) ? visualSpec.cells : [];
  const gridSize = Number(visualSpec.gridSize ?? 3);
  const classic = visualSpec.style === "classicLight";
  return (
    <div
      className={classic ? "grid aspect-square w-full gap-0 rounded-lg border-4 border-slate-950 bg-slate-950 p-0" : "grid aspect-square w-full gap-2 rounded-lg border border-white/10 bg-slate-950/35 p-2"}
      style={{ maxWidth: gridSize > 3 ? "28rem" : "24rem", gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
    >
      {cells.flatMap((row, rowIndex) =>
        (Array.isArray(row) ? row : []).map((cell, columnIndex) => (
          <div key={`${rowIndex}-${columnIndex}`} className={classic ? "flex min-h-16 items-center justify-center border-2 border-slate-950 bg-slate-50" : "flex min-h-16 items-center justify-center rounded-lg border border-white/10 bg-white/[0.045]"}>
            <MatrixCell cell={cell} classic={classic} />
          </div>
        ))
      )}
    </div>
  );
}
